import dayjs from 'dayjs';
import { saveCsvFileWithShiftJIS } from 'src/common/helpers/csv';
import TextConstants from 'src/common/helpers/TextConstants';
import { roundMeasureValue } from 'src/common/helpers/Utils';

const getSenorType = (sensorType) => {
  switch (sensorType) {
    case 0:
      return TextConstants.LoggersManagement.SensorTypeTexts
        .TemperatureBuiltInSensor;
    case 1:
      return TextConstants.LoggersManagement.SensorTypeTexts
        .TemperatureExternal;
    case 2:
      return TextConstants.LoggersManagement.SensorTypeTexts
        .TemperatureAndHumidity;
    case 3:
      return TextConstants.LoggersManagement.SensorTypeTexts.Pt100;
    case 4:
      return TextConstants.LoggersManagement.SensorTypeTexts.Voltage;
    case 5:
      return TextConstants.LoggersManagement.SensorTypeTexts.Pulse;
    case 6:
      return TextConstants.LoggersManagement.SensorTypeTexts.KThermocouple;
    case 7:
      return TextConstants.LoggersManagement.SensorTypeTexts.TThermocouple;

    default:
      return '';
  }
};

const getCH1Unit = (sensorType) => {
  switch (sensorType) {
    //「温度(センサ内蔵)」, 0
    case 0:
    //「温度(外付け)」, 1
    case 1:
    //「温湿度」, 2
    case 2:
    //「Pt100」, 3
    case 3:
    //「K熱電対」, 6
    case 6:
    //「T熱電対」, 7
    case 7:
      return TextConstants.Common.SensorUnit.Temperature;
    //「電圧」, 4
    case 4:
      return TextConstants.Common.SensorUnit.Voltage;
    //「パルス」, 5
    case 5:
      return TextConstants.Common.SensorUnit.Pulse;
    default:
      return '';
  }
};

const getCH2Unit = (sensorType) => {
  switch (sensorType) {
    //「温湿度」, 2
    case 2:
      return TextConstants.Common.SensorUnit.Humidity;

    default:
      return '';
  }
};

const getChannelDisplay = (channelId, channels = [], sensorType = null) => {
  const channel = channels.find((c) => c.channelId === channelId);
  if (!channel) {
    switch (channelId) {
      case 'CH1':
        return `CH1${getCH1Unit(sensorType)}`;
      case 'CH2':
        return `CH2${getCH2Unit(sensorType)}`;
      default:
        return channelId;
    }
  }
  return `${channel.channelName || channelId}${channel.dataUnit || ''}`;
};

const getHeaderCellData = (logger, key) => {
  switch (key) {
    case 'lts':
      return '日時';
    case 'ch1':
      return `CH1 ${getCH1Unit(logger.sty)}`;
    // return getChannelDisplay('CH1', logger.channels, logger.sty);
    case 'ch2':
      return `CH2 ${getCH2Unit(logger.sty)}`;
    // return getChannelDisplay('CH2', logger.channels, logger.sty);
    case 'evf':
      return 'イベント';
    case 'ch1uulav':
      return '上上限';
    case 'ch1ulav':
      return '上限';
    case 'ch1llav':
      return '下限';
    case 'ch1lllav':
      return '下下限';
    case 'ch1uulad':
      return '上上限';
    case 'ch1ulad':
      return '上限';
    case 'ch1llad':
      return '下限';
    case 'ch1lllad':
      return '下下限';
    case 'ch2uulav':
      return '上上限';
    case 'ch2ulav':
      return '上限';
    case 'ch2llav':
      return '下限';
    case 'ch2lllav':
      return '下下限';
    case 'ch2uulad':
      return '上上限';
    case 'ch2ulad':
      return '上限';
    case 'ch2llad':
      return '下限';
    case 'ch2lllad':
      return '下下限';
    case 'lat':
      return '緯度';
    case 'lng':
      return '経度';
    case 'sno':
      return 'No';
    default:
      return '';
  }
};

const getContentCellData = (logger, key, record) => {
  const sensorError = parseInt(record.asi);
  // "0": "全てOK",
  // "1": "1chのみ異常",
  // "2": "2chのみ異常",
  // "3": "3chのみ異常",
  // "4": "1,2chが異常",
  // "5": "1,3chが異常",
  // "6": "2,3chが異常",
  // "7": "全て異常",

  switch (key) {
    case 'lts':
      return record.lts ?? '--';
    case 'ch1':
      if (record.ch1 != 0 && !record.ch1) {
        return '--';
      }

      // missing point display
      if (record.ch1 === '***') {
        return '***';
      }

      const ch1Unit = getCH1Unit(record.sty);
      const ch1value = parseFloat(record.ch1);

      // abnomal range -2100 ~ -2093
      // 機能仕様_20241206 Logger List Func 18
      if (-2100 <= ch1value && ch1value <= -2093) {
        return (
          TextConstants.Common.AbnormalMeasurements[String(ch1value)] || 'ERR'
        );
      }

      return roundMeasureValue(ch1Unit, ch1value);
    case 'ch2':
      if (record.ch2 != 0 && !record.ch2) {
        return '--';
      }

      if (record.ch2 === '***') {
        return '***';
      }

      // format CH2 0 = OFF, 1 = ON For Sensor Type 3 (p100)
      if (record.sty === 3) {
        if (record.ch2 === 0) {
          return 'OFF';
        }
        if (record.ch2 === 1) {
          return 'ON';
        }
      }

      const ch2Unit = getCH2Unit(record.sty);
      const ch2value = parseFloat(record.ch2);

      // abnomal range -2100 ~ -2093
      // 機能仕様_20241206 Logger List Func 18
      if (-2100 <= ch2value && ch2value <= -2093) {
        return (
          TextConstants.Common.AbnormalMeasurements[String(ch2value)] || 'ERR'
        );
      }

      return roundMeasureValue(ch2Unit, ch2value);
    case 'evf':
      return record.evf ?? '';
    case 'ch1uulav':
      return logger.cas?.['1']?.[0] ?? 0;
    case 'ch1ulav':
      return logger.cas?.['1']?.[2] ?? 0;
    case 'ch1llav':
      return logger.cas?.['1']?.[4] ?? 0;
    case 'ch1lllav':
      return logger.cas?.['1']?.[6] ?? 0;
    case 'ch1uulad':
      return logger.cas?.['1']?.[1] ?? '';
    case 'ch1ulad':
      return logger.cas?.['1']?.[3] ?? '';
    case 'ch1llad':
      return logger.cas?.['1']?.[5] ?? '';
    case 'ch1lllad':
      return logger.cas?.['1']?.[7] ?? '';
    case 'ch2uulav':
      return logger.cas?.['2']?.[0] ?? 0;
    case 'ch2ulav':
      return logger.cas?.['2']?.[2] ?? 0;
    case 'ch2llav':
      return logger.cas?.['2']?.[4] ?? 0;
    case 'ch2lllav':
      return logger.cas?.['2']?.[6] ?? 0;
    case 'ch2uulad':
      return logger.cas?.['2']?.[1] ?? '';
    case 'ch2ulad':
      return logger.cas?.['2']?.[3] ?? '';
    case 'ch2llad':
      return logger.cas?.['2']?.[5] ?? '';
    case 'ch2lllad':
      return logger.cas?.['2']?.[7] ?? '';
    case 'lat':
      return record.crd?.[1] ?? '--';
    case 'lng':
      return record.crd?.[2] ?? '--';
    case 'sno':
      return record.sno ?? '';
    default:
      return '';
  }
};

// const buildUnifyLoggerData = (columns = [], data = []) => {
//   const mappedRecords = {};
//   for (const [i, { logger, records }] of data.entries()) {
//     for (const record of records) {
//       let ci = i * columns.length;
//       if (!mappedRecords.hasOwnProperty(record.lts)) {
//         // fill blank
//         mappedRecords[record.lts] = [];
//         for (const __ of data) {
//           mappedRecords[record.lts].push(
//             '--',
//             '--',
//             ...Array(columns.length - 2).fill('')
//           );
//         }
//       }

//       for (const [j, key] of columns.entries()) {
//         mappedRecords[record.lts][ci + j] = getContentCellData(
//           logger,
//           key,
//           record
//         );
//       }
//     }
//   }

//   const rows = [];
//   for (const [ts, cells] of Object.entries(mappedRecords)) {
//     rows.push([ts, ...cells]);
//   }

//   return rows;
// };

// const buildSeparateLoggerData = (columns = [], data = []) => {
//   const rows = [];
//   for (const [i, { logger, records }] of data.entries()) {
//     for (const [ri, record] of records.entries()) {
//       let ci = i * columns.length;
//       if (!rows[ri]) {
//         rows[ri] = [];
//       }
//       for (const [j, key] of columns.entries()) {
//         rows[ri][ci + j] = getContentCellData(logger, key, record);
//       }
//     }
//   }

//   return rows;
// };

const buildCSVHeaders = (settings = {}, data = []) => {
  const columns = [];
  const cells = Array(4).fill([]);

  const firstLineCells = ['センサ種', 'sty'];
  const secondLineCells = ['ロガー名称', 'lna'];
  const thirdLineCells = ['ロガーID', 'deviceId'];

  for (const { logger } of data) {
    // replace logger values
    firstLineCells[1] = getSenorType(logger.sty);
    secondLineCells[1] = logger.lna ?? '';
    thirdLineCells[1] = logger.deviceId;

    const loggerColumns = ['lts'];

    loggerColumns.push('ch1');
    /**
     * 2: temperature and humidity
     * 3: pt100
     * other sensor type have only 1 channel
     */
    if ([2, 3].includes(parseInt(logger.sty))) {
      loggerColumns.push('ch2');
    }

    const headers = [];
    for (const column of loggerColumns) {
      headers.push(getHeaderCellData(logger, column));
    }

    // sensor type
    cells[0] = [
      ...cells[0],
      ...firstLineCells,
      ...(loggerColumns.length > firstLineCells.length
        ? Array(loggerColumns.length - firstLineCells.length).fill('')
        : []),
    ];

    // logger name
    cells[1] = [
      ...cells[1],
      ...secondLineCells,
      ...(loggerColumns.length > secondLineCells.length
        ? Array(loggerColumns.length - secondLineCells.length).fill('')
        : []),
    ];

    // logger id
    cells[2] = [
      ...cells[2],
      ...thirdLineCells,
      ...(loggerColumns.length > thirdLineCells.length
        ? Array(loggerColumns.length - thirdLineCells.length).fill('')
        : []),
    ];

    // ch headers
    cells[3] = [...cells[3], ...headers];

    columns.push(loggerColumns);
  }

  return [columns, cells];
};

// const buildCSVHeaders = (settings = {}, data = []) => {
//   const columns = ['ch1', 'ch2'];
//   const cells = settings.isUnify
//     ? [[''], [''], [''], ['日時']]
//     : Array(4).fill([]);

//   const firstLineCells = ['センサ種', 'sty'];
//   const secondLineCells = ['ロガー名称', 'lna'];
//   const thirdLineCells = ['ロガーID', 'deviceId'];

//   if (settings.displayEventFlag) {
//     thirdLineCells.push('');
//     columns.push('evf');
//   }

//   if (!settings.isUnify) {
//     thirdLineCells.push('');
//     columns.unshift('lts');
//   }

//   if (settings.displayDeviations && settings.displayAlarmSettings) {
//     thirdLineCells.push(
//       'CH1逸脱',
//       ...Array(3).fill(''),
//       'CH1警報',
//       ...Array(3).fill(''),
//       'CH2逸脱',
//       ...Array(3).fill(''),
//       'CH2警報'
//     );
//     columns.push(
//       'ch1uulad',
//       'ch1ulad',
//       'ch1llad',
//       'ch1lllad',
//       'ch1uulav',
//       'ch1ulav',
//       'ch1llav',
//       'ch1lllav',
//       'ch2uulad',
//       'ch2ulad',
//       'ch2llad',
//       'ch2lllad',
//       'ch2uulav',
//       'ch2ulav',
//       'ch2llav',
//       'ch2lllav'
//     );
//   } else if (settings.displayDeviations) {
//     thirdLineCells.push('CH1逸脱', ...Array(3).fill(''), 'CH2逸脱');
//     columns.push(
//       'ch1uulad',
//       'ch1ulad',
//       'ch1llad',
//       'ch1lllad',
//       'ch2uulad',
//       'ch2ulad',
//       'ch2llad',
//       'ch2lllad'
//     );
//   } else if (settings.displayAlarmSettings) {
//     thirdLineCells.push('CH1警報', ...Array(3).fill(''), 'CH2警報');
//     columns.push(
//       'ch1uulav',
//       'ch1ulav',
//       'ch1llav',
//       'ch1lllav',
//       'ch2uulav',
//       'ch2ulav',
//       'ch2llav',
//       'ch2lllav'
//     );
//   }

//   if (settings.displayGPS) {
//     columns.push('lat', 'lng');
//   }

//   // columns.push('sno');

//   for (const { logger } of data) {
//     // replace logger values
//     firstLineCells[1] = getSenorType(logger.sty);
//     secondLineCells[1] = logger.lna ?? '';
//     thirdLineCells[1] = logger.deviceId;

//     const headers = [];
//     for (const column of columns) {
//       headers.push(getHeaderCellData(logger, column));
//     }

//     // sensor type
//     cells[0] = [
//       ...cells[0],
//       ...firstLineCells,
//       ...Array(columns.length - firstLineCells.length).fill(''),
//     ];

//     // logger name
//     cells[1] = [
//       ...cells[1],
//       ...secondLineCells,
//       ...Array(columns.length - secondLineCells.length).fill(''),
//     ];

//     // logger id
//     cells[2] = [
//       ...cells[2],
//       ...thirdLineCells,
//       ...Array(columns.length - thirdLineCells.length).fill(''),
//     ];
//     // ch headers
//     cells[3] = [...cells[3], ...headers];
//   }

//   return [columns, cells];
// };

// const buildCSVContentRecords = (settings = {}, columns = [], data = []) => {
//   if (settings.isUnify) {
//     return buildUnifyLoggerData(columns, data);
//   } else {
//     return buildSeparateLoggerData(columns, data);
//   }
// };

const buildCSVContentRecords = (columns = [], data = []) => {
  const rows = [];
  for (const [i, { logger, records }] of data.entries()) {
    for (const [ri, record] of records.entries()) {
      // total columns from first logger to current logger
      let ci = columns.slice(0, i).reduce((val, arr) => val + arr.length, 0);
      if (!rows[ri]) {
        rows[ri] = [];
      }
      for (const [j, key] of columns[i].entries()) {
        rows[ri][ci + j] = getContentCellData(logger, key, record);
      }
    }
  }

  return rows;
};

export const downloadCSVFileData = (settings = {}, data = []) => {
  const [columns, headers] = buildCSVHeaders(settings, data);

  const records = buildCSVContentRecords(columns, data);

  const lines = [...headers, ...records];

  // convert to csv string format
  let csvData = '';
  for (const cells of lines) {
    csvData += cells.join(',') + '\r\n';
  }

  saveCsvFileWithShiftJIS(csvData, `${dayjs().format('YYYYMMDDHHmmss')}.csv`);
};
